var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { camelizeKeys } from "humps";
import { equals, isNil, join, propOr } from "ramda";
import { TrackJS } from "trackjs";
import { fetchCalculatedTotals } from "actions/online_ordering/calculate_totals";
import { parseJSON } from "helpers/ajax_request";
import { ERROR_MAPPINGS, GENERIC_ERROR, } from "helpers/online_ordering/checkout";
import { Money } from "@upserve/financials";
import { RequestStatus } from "types/requests";
import { getPrettyUrl } from "get_base_uri";
import uuid from "uuid/v4";
import { addGiftCardToList, removeGiftCardFromListByIndex, } from "helpers/online_ordering/cart/gift_card";
import { selectGiftCards } from "selectors";
import { getGiftCardBalance } from "interfaces/calculate_totals";
export var UPDATE_CONTACT_INFO = "online_ordering/checkout/UPDATE_CONTACT_INFO";
export var UPDATE_NEW_CREDIT_CARD = "online_ordering/checkout/UPDATE_NEW_CREDIT_CARD";
export var UPDATE_PAYMENT_TYPE = "online_ordering/checkout/UPDATE_PAYMENT_TYPE";
export var UPDATE_ORDER_STATUS = "online_ordering/checkout/UPDATE_ORDER_STATUS";
export var UPDATE_ORDER_STATUS_WITH_ERROR = "online_ordering/checkout/UPDATE_ORDER_STATUS_WITH_ERROR";
export var ADD_ORDER_CONFIRMATION_DETAILS = "online_ordering/checkout/ADD_ORDER_CONFIRMATION_DETAILS";
export var STORE_TYPE_AND_LAST_FOUR = "online_ordering/checkout/STORE_TYPE_AND_LAST_FOUR";
export var PROCEED_TO_CHECKOUT = "online_ordering/checkout/PROCEED_TO_CHECKOUT";
export var RETURN_TO_MENU = "online_ordering/checkout/RETURN_TO_MENU";
export var SELECT_CARD_FROM_DROPDOWN = "online_ordering/checkout/SELECT_CARD_FROM_DROPDOWN";
export var UPDATE_DELIVERY_INSTRUCTIONS = "online_ordering/checkout/UPDATE_DELIVERY_INSTRUCTIONS";
export var UPDATE_IS_CURBSIDE_PICKUP = "online_ordering/checkout/UPDATE_IS_CURBSIDE_PICKUP";
export var UPDATE_CURBSIDE_PICKUP_INSTRUCTIONS = "online_ordering/checkout/UPDATE_CURBSIDE_PICKUP_INSTRUCTIONS";
export var APPLY_ORDER_ADDRESS_FROM_LOCAL_STORAGE = "online_ordering/checkout/APPLY_ORDER_ADDRESS_FROM_LOCAL_STORAGE";
export var SET_PAYMENT_GIFT_CARD_REQUEST = "online_ordering/checkout/SET_PAYMENT_GIFT_CARD_REQUEST";
export var SET_PAYMENT_GIFT_CARD_INFO = "online_ordering/checkout/SET_PAYMENT_GIFT_CARD_INFO";
export var UPDATE_TIP = "online_ordering/checkout/UPDATE_TIP";
export var UPDATE_SELECTED_TIP_OPTION = "online_ordering/checkout/UPDATE_SELECTED_TIP_OPTION";
function updateContactInfo(contactInfo) {
    return {
        type: UPDATE_CONTACT_INFO,
        contactInfo: contactInfo,
    };
}
function selectFromCardDropdown(cardOption) {
    return {
        type: SELECT_CARD_FROM_DROPDOWN,
        cardOption: cardOption,
    };
}
function updateNewCreditCard(newCreditCard) {
    return {
        type: UPDATE_NEW_CREDIT_CARD,
        newCreditCard: newCreditCard,
    };
}
function updatePaymentType(cards, paymentType) {
    if (cards === void 0) { cards = []; }
    return {
        type: UPDATE_PAYMENT_TYPE,
        paymentType: paymentType,
        cards: cards,
    };
}
function updateOrderStatus(status) {
    return {
        type: UPDATE_ORDER_STATUS,
        status: status,
    };
}
function parseOrderError(errorSource, response, fallbackMessage) {
    if (fallbackMessage === void 0) { fallbackMessage = GENERIC_ERROR; }
    return function (dispatch) {
        return new Promise(function (resolve, reject) {
            return response ? resolve(response) : reject();
        })
            .then(parseJSON)
            .then(camelizeKeys)
            .then(function (_a) {
            var error = _a.error, errors = _a.errors, errorType = _a.errorType;
            var mappedError = fallbackMessage;
            if (equals("submitOrder", errorSource) && !isNil(errors)) {
                // errors will be an array of errors of string
                var orderErrors = errors;
                // For now join the responses as they are already in sentence format.
                mappedError = join(" ", orderErrors);
            }
            else {
                // Handle regular error response
                mappedError = propOr(error, error, ERROR_MAPPINGS); // FIXME - Improve error mapping - See HQ-4459
            }
            dispatch(updateOrderStatusWithError(errorSource, mappedError, equals("menu", errorType)));
            TrackJS.console.error(mappedError, { error: error, errors: errors, errorType: errorType });
        })
            .catch(function () {
            dispatch(updateOrderStatusWithError(errorSource, "Sorry, online ordering is temporarily unavailable.", false));
        });
    };
}
export var parseOrderSubmissionError = parseOrderError.bind(null, "submitOrder");
export var parseOrderCalculationError = parseOrderError.bind(null, "calculateTotal");
function updateOrderStatusWithError(errorSource, error, menuError) {
    return {
        type: UPDATE_ORDER_STATUS_WITH_ERROR,
        error: error,
        errorSource: errorSource,
        menuError: menuError,
    };
}
function orderSuccess(orderCompletionDetails) {
    return {
        type: ADD_ORDER_CONFIRMATION_DETAILS,
        orderCompletionDetails: orderCompletionDetails,
    };
}
function updateDeliveryInstructions(instructions) {
    return {
        type: UPDATE_DELIVERY_INSTRUCTIONS,
        instructions: instructions,
    };
}
function updateIsCurbsidePickup(isCurbsidePickup) {
    return {
        type: UPDATE_IS_CURBSIDE_PICKUP,
        isCurbsidePickup: isCurbsidePickup,
    };
}
function updateCurbsidePickupInstructions(instructions) {
    return {
        type: UPDATE_CURBSIDE_PICKUP_INSTRUCTIONS,
        instructions: instructions,
    };
}
function storeTypeAndLastFour(lastFour, cardType) {
    return {
        type: STORE_TYPE_AND_LAST_FOUR,
        lastFour: lastFour,
        cardType: cardType,
    };
}
function proceedToCheckout(paymentOptions) {
    return {
        type: PROCEED_TO_CHECKOUT,
        paymentOptions: paymentOptions,
    };
}
function returnToMenu() {
    return {
        type: RETURN_TO_MENU,
    };
}
function applyOrderAddressFromLocalStorage(address) {
    return {
        type: APPLY_ORDER_ADDRESS_FROM_LOCAL_STORAGE,
        address: address,
    };
}
function setPaymentGiftCardRequestStatus(status) {
    return {
        type: SET_PAYMENT_GIFT_CARD_REQUEST,
        status: status,
    };
}
function SetPaymentGiftCardInfo(giftCards) {
    return {
        type: SET_PAYMENT_GIFT_CARD_INFO,
        giftCards: giftCards,
    };
}
function applyGiftCard(newGiftCard) {
    var index = newGiftCard.index, giftCardNumber = newGiftCard.giftCardNumber, pin = newGiftCard.pin;
    var giftCard = {
        index: index,
        giftCardNumber: giftCardNumber,
        balance: Money(0),
        pin: pin,
    };
    var requestId = uuid();
    return function (dispatch, getState) {
        dispatch(setPaymentGiftCardRequestStatus(RequestStatus.REQUESTING));
        var giftCardForBalanceRequest = {
            giftCode: giftCard.giftCardNumber,
            pin: giftCard.pin,
            storePrettyUrl: getPrettyUrl(),
            requestId: requestId,
        };
        getGiftCardBalance(giftCardForBalanceRequest)
            .then(function (_a) {
            var balance = _a.balance;
            giftCard = __assign(__assign({}, giftCard), { balance: Money(balance) });
            var giftCards = selectGiftCards(getState()).giftCards;
            dispatch(SetPaymentGiftCardInfo(addGiftCardToList(giftCard, giftCards)));
            dispatch(fetchCalculatedTotals(0));
        })
            .catch(function (error) {
            dispatch(setPaymentGiftCardRequestStatus(RequestStatus.FAILURE));
            TrackJS.track("error fetching balance for gift card - request id: " + requestId);
            TrackJS.track("ubergateway error response: " + error);
        });
    };
}
function removeGiftCard(index) {
    return function (dispatch, getState) {
        dispatch(setPaymentGiftCardRequestStatus(RequestStatus.REQUESTING));
        var giftCards = selectGiftCards(getState()).giftCards;
        dispatch(SetPaymentGiftCardInfo(removeGiftCardFromListByIndex(index, giftCards)));
        dispatch(fetchCalculatedTotals(0));
    };
}
function updateTip(tip) {
    return {
        type: UPDATE_TIP,
        tip: tip,
    };
}
function updateSelectedtipOption(selectedTipOption) {
    return {
        type: UPDATE_SELECTED_TIP_OPTION,
        selectedTipOption: selectedTipOption,
    };
}
export { applyGiftCard, applyOrderAddressFromLocalStorage, orderSuccess, proceedToCheckout, removeGiftCard, returnToMenu, selectFromCardDropdown, setPaymentGiftCardRequestStatus, storeTypeAndLastFour, updateContactInfo, updateCurbsidePickupInstructions, updateDeliveryInstructions, updateIsCurbsidePickup, updateNewCreditCard, updateOrderStatus, updateOrderStatusWithError, updatePaymentType, updateSelectedtipOption, updateTip, };
